import { useState } from 'react';
import type { UserRole } from '@bridgewerk/shared';
import { PageContainer } from '../../components/layout/PageContainer.js';
import { Button } from '../../components/ui/Button.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useToast } from '../../components/ui/Toast.js';
import { useAuth } from '../auth/useAuth.js';
import { useMachines } from '../../contexts/MachineContext.js';
import { useMode } from '../../contexts/ModeContext.js';
import { useTeamUsers, useUpdateTeamUser, useRemoveTeamUser } from './hooks/useTeam.js';
import { InviteModal } from './components/InviteModal.js';
import { AssignmentMatrix } from './components/AssignmentMatrix.js';

const ROLE_LABEL: Record<UserRole, string> = {
  manager:    'Manager',
  programmer: 'Programmer',
  operator:   'Operator',
};

const ROLE_COLOR: Record<UserRole, string> = {
  manager:    'text-accent border-accent/30 bg-accent/10',
  programmer: 'text-yellow border-yellow/30 bg-yellow/10',
  operator:   'text-green border-green/30 bg-green/10',
};

export function TeamManagementPage(): JSX.Element {
  const { shopId, isManager } = useAuth();
  const { machines } = useMachines();
  const { startImpersonating } = useMode();
  const toast = useToast();

  const [showInvite, setShowInvite] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<string | null>(null);

  const teamQuery = useTeamUsers(shopId);
  const updateUser = useUpdateTeamUser(shopId);
  const removeUser = useRemoveTeamUser(shopId);

  const users  = teamQuery.data?.users  ?? [];
  const invites = teamQuery.data?.invites ?? [];

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      await updateUser.mutateAsync({ userId, patch: { role: newRole } });
      setEditingRole(null);
      toast.push({ tone: 'success', title: 'Role updated' });
    } catch (err) {
      toast.push({ tone: 'critical', title: 'Failed to update role', description: err instanceof Error ? err.message : String(err) });
    }
  };

  const handleDeactivate = async (userId: string) => {
    try {
      await removeUser.mutateAsync(userId);
      setConfirmDeactivate(null);
      toast.push({ tone: 'success', title: 'User deactivated' });
    } catch (err) {
      toast.push({ tone: 'critical', title: 'Failed to deactivate', description: err instanceof Error ? err.message : String(err) });
      setConfirmDeactivate(null);
    }
  };

  const handleViewAs = (userId: string) => {
    const user = users.find(u => u.user_id === userId);
    if (!user) return;
    const machineIds = user.assigned_machines.map(a => a.machine_id);
    startImpersonating({
      userId: user.user_id,
      role: user.role,
      assignedMachineIds: user.role === 'manager' ? null : new Set(machineIds),
    });
    toast.push({ tone: 'info', title: `Viewing as ${user.display_name ?? user.email}` });
  };

  if (!isManager) {
    return (
      <PageContainer>
        <p className="text-sm font-mono text-text-dim">Only managers can access team settings.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-mono text-base">Team</h1>
          <p className="text-xs text-text-dim mt-0.5">Manage members, roles, and machine assignments.</p>
        </div>
        <Button variant="primary" onClick={() => setShowInvite(true)}>
          + Invite
        </Button>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Pending invites */}
        {invites.length > 0 && (
          <Section title="Pending invitations">
            <div className="space-y-2">
              {invites.map(inv => (
                <div
                  key={inv.invite_id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-md border border-dashed border-border"
                >
                  <div>
                    <p className="text-sm font-mono text-text">{inv.email}</p>
                    <p className="text-[10px] font-mono text-text-dim">
                      Invited as {ROLE_LABEL[inv.role]} · expires {formatDate(inv.expires_at)}
                    </p>
                  </div>
                  <span className="text-[10px] font-mono text-muted uppercase tracking-wide">Pending</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Active users */}
        <Section title="Team members">
          {teamQuery.isLoading && (
            <div className="space-y-2">
              {[0, 1, 2].map(i => <Skeleton key={i} className="h-14 rounded-md" />)}
            </div>
          )}

          {!teamQuery.isLoading && users.filter(u => u.is_active).length === 0 && (
            <p className="text-xs font-mono text-muted py-2">No active team members yet.</p>
          )}

          <div className="space-y-2">
            {users.filter(u => u.is_active).map(u => (
              <div
                key={u.user_id}
                className="rounded-md border border-border bg-card/30 overflow-hidden"
              >
                <div className="flex items-center justify-between px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-mono text-text truncate">
                        {u.display_name ?? u.email}
                      </p>
                      {editingRole === u.user_id ? (
                        <select
                          defaultValue={u.role}
                          onChange={e => void handleRoleChange(u.user_id, e.target.value as UserRole)}
                          onBlur={() => setEditingRole(null)}
                          autoFocus
                          className="rounded border border-accent bg-card px-1.5 py-0.5 text-[11px] font-mono text-text focus:outline-none"
                        >
                          <option value="manager">Manager</option>
                          <option value="programmer">Programmer</option>
                          <option value="operator">Operator</option>
                        </select>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingRole(u.user_id)}
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded border capitalize ${ROLE_COLOR[u.role]}`}
                          title="Click to change role"
                        >
                          {ROLE_LABEL[u.role]}
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] font-mono text-text-dim">{u.email}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {u.role !== 'manager' && (
                      <button
                        type="button"
                        onClick={() => handleViewAs(u.user_id)}
                        className="text-[11px] font-mono text-text-dim hover:text-accent transition-colors"
                        title="View app as this user"
                      >
                        View As
                      </button>
                    )}
                    {confirmDeactivate === u.user_id ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-text-dim">Remove?</span>
                        <button
                          type="button"
                          onClick={() => void handleDeactivate(u.user_id)}
                          disabled={removeUser.isPending}
                          className="text-[11px] font-mono text-red hover:underline disabled:opacity-50"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeactivate(null)}
                          className="text-[11px] font-mono text-text-dim hover:text-text"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeactivate(u.user_id)}
                        className="text-[11px] font-mono text-muted hover:text-red transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {u.assigned_machines.length > 0 && (
                  <div className="px-3 pb-2.5 flex flex-wrap gap-1">
                    {u.assigned_machines.map(a => (
                      <span
                        key={a.machine_id}
                        className="text-[10px] font-mono text-text-dim px-1.5 py-0.5 rounded border border-border"
                      >
                        {a.machine_id}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Assignment matrix */}
        {machines.length > 0 && users.some(u => u.role !== 'manager') && (
          <Section title="Machine assignments">
            <AssignmentMatrix
              shopId={shopId!}
              machines={machines}
              users={users}
            />
          </Section>
        )}
      </div>

      {showInvite && (
        <InviteModal
          shopId={shopId!}
          machines={machines}
          onClose={() => setShowInvite(false)}
          onSuccess={() => {
            setShowInvite(false);
            toast.push({ tone: 'success', title: 'Invitation sent' });
            void teamQuery.refetch();
          }}
        />
      )}
    </PageContainer>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-dim mb-3">{title}</h2>
      <div className="rounded-xl border border-border bg-surface overflow-hidden">
        <div className="px-4 py-4">{children}</div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
